import React, { createContext, useState, useEffect, useContext } from 'react'
import { cartAPI } from '../api'
import { publicApi } from '../api'
import { useAuth } from './AuthContext'
import { toast } from 'react-hot-toast'

const CartContext = createContext()

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) throw new Error('useCart must be used within a CartProvider')
  return context
}

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null)
  const [loading, setLoading] = useState(false)
  const [itemCount, setItemCount] = useState(0)
  const [couponMeta, setCouponMeta] = useState(null)
  const [couponBreakdown, setCouponBreakdown] = useState([])
  const { isAuthenticated, user } = useAuth()

  useEffect(() => {
    if (isAuthenticated && user?.role === 'CUSTOMER') {
      loadCart()
    } else if (!isAuthenticated) {
      loadGuestCart()
    }
  }, [isAuthenticated, user])


  const syncCart = (cartData) => {
    if (!cartData) {
      setCart(null)
      setItemCount(0)
      setCouponMeta(null)
      return
    }


    if (!cartData.couponCodes) {
      cartData.couponCodes = cartData.couponCode ? [cartData.couponCode] : []
    }

    setCart(cartData)
    setItemCount(cartData?.itemCount || 0)

    if (!cartData.couponCodes?.length) {
      setCouponMeta(null)
      setCouponBreakdown([])
    }
  }


  const revalidateCoupons = async (cartData) => {
    if (!cartData?.couponCodes?.length) return cartData

    const newSubtotal = Number(cartData.subtotal ?? 0)
    const items = (cartData.items || []).map(item => ({
      productId: item.productId,
      quantity: item.quantity,
    }))

    for (const code of [...cartData.couponCodes]) {
      try {
        await publicApi.post('/orders/validate-coupon', {
          couponCode: code,
          subtotal: newSubtotal,
          items,
        })
      } catch {
        toast.error(`Coupon "${code}" removed — conditions no longer met`)
        try {
          cartData = await cartAPI.removeCoupon(code)
          if (!cartData.couponCodes) {
            cartData.couponCodes = cartData.couponCode ? [cartData.couponCode] : []
          }
        } catch {
          cartData = { ...cartData }
        }
        cartData.couponCodes = (cartData.couponCodes || []).filter(c => c !== code)
        cartData.couponCode = null
        setCouponMeta(null)
      }
    }

    return cartData
  }

  const refreshCouponBreakdown = async (cartData) => {
    const codes = cartData?.couponCodes?.length
      ? cartData.couponCodes
      : cartData?.couponCode
        ? [cartData.couponCode]
        : []

    if (!codes.length) {
      setCouponBreakdown([])
      return cartData
    }

    const subtotal = Number(cartData.subtotal ?? 0)
    const items = (cartData.items || []).map(item => ({
      productId: item.productId,
      quantity: item.quantity,
    }))

    const breakdown = []
    const validCodes = []

    for (const code of codes) {
      try {
        const res = await publicApi.post('/orders/validate-coupon', {
          couponCode: code,
          subtotal,
          items,
        })
        const meta = res.data?.data ?? res.data
        breakdown.push({ ...meta, code })
        validCodes.push(code)
      } catch {
      }
    }

    setCouponBreakdown(breakdown)

    if (validCodes.length !== codes.length) {
      cartData = { ...cartData, couponCodes: validCodes }
    }

    return cartData
  }

  const loadCart = async () => {
    try {
      setLoading(true)
      let cartData = await cartAPI.getCart()
      cartData = await revalidateCoupons(cartData)
      cartData = await refreshCouponBreakdown(cartData)
      syncCart(cartData)

    } catch (error) {
      console.error('Failed to load cart:', error)
    } finally {
      setLoading(false)
    }
  }

  const getManualEntries = (guestCart) =>
    couponBreakdown.filter(m => !m.automatic && (guestCart.couponCodes || []).includes(m.code))
  const applyGuestAutomaticCoupons = async (guestCart, manualEntries = getManualEntries(guestCart)) => {
    try {
      const items = guestCart.items || []
      const productIds = items.map(i => i.productId)
      const categoryIds = [...new Set(items.map(i => i.categoryId).filter(Boolean))]
      const productQuantities = {}
      for (const item of items) {
        productQuantities[item.productId] = (productQuantities[item.productId] || 0) + item.quantity
      }
      const res = await publicApi.post('/coupons/automatic', {
        subtotal: guestCart.subtotal,
        productIds,
        categoryIds,
        productQuantities,
      })
      const autoCoupons = res.data?.data ?? res.data ?? []

      const getQualifyingSubtotal = (cartItems, coupon) => {
        const hasProductRestriction = coupon.applicableProducts?.length > 0
        const hasCategoryRestriction = coupon.applicableCategories?.length > 0
        if (!hasProductRestriction && !hasCategoryRestriction) {
          return cartItems.reduce((sum, i) => sum + (Number(i.price) * i.quantity), 0)
        }
        return cartItems.reduce((sum, item) => {
          const productMatch = hasProductRestriction && coupon.applicableProducts.includes(item.productId)
          const categoryMatch = hasCategoryRestriction && item.categoryId != null
            && coupon.applicableCategories.includes(item.categoryId)
          let qualifies
          if (hasProductRestriction && hasCategoryRestriction) qualifies = productMatch || categoryMatch
          else if (hasProductRestriction) qualifies = productMatch
          else qualifies = categoryMatch
          return qualifies ? sum + (Number(item.price) * item.quantity) : sum
        }, 0)
      }

      const autoBreakdown = autoCoupons.map(coupon => {
        const qualifyingSubtotal = getQualifyingSubtotal(items, coupon)
        let discountAmount = 0
        if (coupon.type === 'PERCENTAGE') {
          discountAmount = qualifyingSubtotal * (coupon.discountValue / 100)
          if (coupon.maximumDiscountAmount) {
            discountAmount = Math.min(discountAmount, coupon.maximumDiscountAmount)
          }
        } else if (coupon.type === 'FIXED_AMOUNT') {
          discountAmount = Math.min(coupon.discountValue, qualifyingSubtotal)
        }
        return {
          code: coupon.code,
          type: coupon.type,
          discountValue: coupon.discountValue,
          discountAmount,
          freeShipping: coupon.type === 'FREE_SHIPPING',
          automatic: true,
        }
      })

      const combinedBreakdown = [...autoBreakdown, ...manualEntries]
      guestCart.couponCodes = combinedBreakdown.map(m => m.code)

      const combinedDiscount = combinedBreakdown.reduce(
        (sum, m) => sum + (m.freeShipping ? 0 : Number(m.discountAmount ?? 0)), 0
      )
      guestCart.discountAmount = Math.min(combinedDiscount, guestCart.subtotal)
      guestCart.total = Math.max(0, guestCart.subtotal - guestCart.discountAmount)

      setCouponBreakdown(combinedBreakdown)
      localStorage.setItem('guestCouponBreakdown', JSON.stringify(combinedBreakdown))
    } catch (e) {
      console.error('Failed to check automatic coupons:', e)
    }
    return guestCart
  }

  const loadGuestCart = async () => {
    let parsed = null
    const guestCartRaw = localStorage.getItem('guestCart')
    if (guestCartRaw) {
      try {
        parsed = JSON.parse(guestCartRaw)
        setCart(parsed)
        setItemCount(parsed.itemCount || 0)
      } catch {
        localStorage.removeItem('guestCart')
      }
    }

    let savedBreakdown = []
    const savedBreakdownRaw = localStorage.getItem('guestCouponBreakdown')
    if (savedBreakdownRaw) {
      try {
        savedBreakdown = JSON.parse(savedBreakdownRaw)
        setCouponBreakdown(savedBreakdown)
      } catch {
        localStorage.removeItem('guestCouponBreakdown')
      }
    }

    if (!parsed?.items?.length) return

    const manualCodes = (parsed.couponCodes || []).filter(
      code => !savedBreakdown.find(m => m.code === code && m.automatic)
    )
    const subtotal = Number(parsed.subtotal ?? 0)
    const validationItems = (parsed.items || []).map(item => ({
      productId: item.productId,
      quantity: item.quantity,
    }))
    const manualBreakdown = []
    for (const code of manualCodes) {
      try {
        const res = await publicApi.post('/orders/validate-coupon', {
          couponCode: code,
          subtotal,
          items: validationItems,
        })
        const meta = res.data?.data ?? res.data
        manualBreakdown.push({ ...meta, code })
      } catch {
      }
    }

    const guestCart = { ...parsed, items: [...(parsed.items || [])] }
    const updatedCart = await applyGuestAutomaticCoupons(guestCart, manualBreakdown)
    saveGuestCart(updatedCart)
  }

  const saveGuestCart = (cartData) => {
    localStorage.setItem('guestCart', JSON.stringify(cartData))
    setCart(cartData)
    setItemCount(cartData.itemCount || 0)
  }

  const addToCart = async (productId, quantity = 1, options = {}) => {
    try {
      if (isAuthenticated) {
        const cartData = await cartAPI.addToCart({
          productId,
          quantity,
          variationId: options.variationId || null,
          addonProductAddOnId: options.addonProductAddOnId || null,
          addonVariationId: options.addonVariationId || null,
        })
        syncCart(cartData)

        // Trigger flying animation
        if (options.onSuccess) {
          options.onSuccess()
        }
      } else {
        let guestCart = cart
          ? { ...cart, items: [...(cart.items || [])] }
          : { items: [], itemCount: 0, subtotal: 0, total: 0, discountAmount: 0, shippingAmount: 0, taxAmount: 0 }

        const existingItem = guestCart.items.find(
          item =>
            item.productId === productId &&
            (item.variationId || null) === (options.variationId || null)
        )

        if (existingItem) {
          existingItem.quantity += quantity
        } else {
          guestCart.items.push({
            id: Date.now(),
            productId,
            quantity,
            productName: options.productName || 'Product',
            productImage: options.productImage || null,
            price: options.price || 0,
            originalPrice: options.price || 0,
            inStock: options.inStock !== undefined ? options.inStock : true,
            stockQuantity: options.stockQuantity || 99,
            variationId: options.variationId || null,
            variantImage: options.variantImage || options.variationImage || null,
            variationName: options.variationName || options.variantName || null,
            color: options.color || null,
            size: options.size || null,
            selectedVariants: options.selectedVariants || null,
            weightGrams: options.weightGrams || 0,
            categoryId: options.categoryId || null,
          })
        }
        guestCart.itemCount = guestCart.items.reduce((sum, i) => sum + i.quantity, 0)
        guestCart.subtotal = guestCart.items.reduce((sum, i) => sum + (Number(i.price) * i.quantity), 0)
        guestCart.total = guestCart.subtotal
        guestCart = await applyGuestAutomaticCoupons(guestCart)
        saveGuestCart(guestCart)

        if (options.onSuccess) {
          options.onSuccess()
        }
      }
    } catch (error) {
      throw error
    }
  }

  const updateCartItem = async (itemId, quantity) => {
    try {
      if (isAuthenticated) {
        let cartData = await cartAPI.updateCartItem(itemId, quantity)
        cartData = await revalidateCoupons(cartData)
        cartData = await refreshCouponBreakdown(cartData)
        syncCart(cartData)
      } else {
        let guestCart = { ...cart, items: [...(cart.items || [])] }
        const item = guestCart.items.find(i => i.id === itemId)
        if (item) {
          if (quantity <= 0) {
            guestCart.items = guestCart.items.filter(i => i.id !== itemId)
          } else {
            item.quantity = quantity
          }
          guestCart.itemCount = guestCart.items.reduce((sum, i) => sum + i.quantity, 0)
          guestCart.subtotal = guestCart.items.reduce((sum, i) => sum + (Number(i.price) * i.quantity), 0)
          guestCart.total = couponMeta
            ? guestCart.subtotal - Number(couponMeta.discountAmount ?? 0)
            : guestCart.subtotal
        }
        guestCart = await applyGuestAutomaticCoupons(guestCart)
        saveGuestCart(guestCart)
      }
    } catch (error) {
      throw error
    }
  }

  const removeFromCart = async (itemId) => {
    try {
      if (isAuthenticated) {
        let cartData = await cartAPI.removeFromCart(itemId)

        // If cart is now empty, clear coupons
        if (!cartData?.items?.length) {
          if (cartData?.couponCodes?.length) {
            for (const code of [...(cartData.couponCodes)]) {
              try { cartData = await cartAPI.removeCoupon(code) } catch { }
            }
          }
          syncCart({ ...cartData, couponCodes: [], discountAmount: 0 })
        } else {
          cartData = await revalidateCoupons(cartData)
          cartData = await refreshCouponBreakdown(cartData)
          syncCart(cartData)
        }
      } else {
        let guestCart = { ...cart, items: cart.items.filter(i => i.id !== itemId) }
        guestCart.itemCount = guestCart.items.reduce((sum, i) => sum + i.quantity, 0)
        guestCart.subtotal = guestCart.items.reduce((sum, i) => sum + (Number(i.price) * i.quantity), 0)
        guestCart.total = guestCart.subtotal

        // Clear coupons if cart is now empty
        if (!guestCart.items.length) {
          delete guestCart.couponCode
          guestCart.couponCodes = []
          guestCart.discountAmount = 0
          localStorage.removeItem('guestCouponMeta')
          setCouponMeta(null)
        } else {
          guestCart = await applyGuestAutomaticCoupons(guestCart)
        }

        saveGuestCart(guestCart)
      }
    } catch (error) {
      throw error
    }
  }

  const clearCart = async () => {
    try {
      if (isAuthenticated) {
        // Remove all coupons first
        if (cart?.couponCodes?.length > 0) {
          for (const code of cart.couponCodes) {
            try { await cartAPI.removeCoupon(code) } catch { }
          }
        }
        await cartAPI.clearCart()
      } else {
        localStorage.removeItem('guestCart')
        localStorage.removeItem('guestCouponMeta')
      }
      setCart(null)
      setItemCount(0)
      setCouponMeta(null)
      setCouponBreakdown([])
    } catch (error) {
      throw error
    }
  }

  const applyCoupon = async (couponCode, meta = null) => {
    try {
      if (isAuthenticated) {
        let cartData = await cartAPI.applyCoupon(couponCode)
        if (!cartData.couponCodes) {
          cartData.couponCodes = cartData.couponCode ? [cartData.couponCode] : []
        }
        cartData = await refreshCouponBreakdown(cartData)
        setCart(cartData)
        setItemCount(cartData?.itemCount || 0)
      } else {
        const guestCart = {
          ...cart,
          couponCode,
          couponCodes: [...(cart?.couponCodes || []), couponCode],
        }
        if (meta) {
          const newBreakdown = [...couponBreakdown, { ...meta, code: couponCode }]
          const combinedDiscount = newBreakdown.reduce(
            (sum, m) => sum + (m.freeShipping ? 0 : Number(m.discountAmount ?? 0)), 0
          )
          guestCart.discountAmount = Math.min(combinedDiscount, guestCart.subtotal)
          guestCart.total = Math.max(0, guestCart.subtotal - guestCart.discountAmount)
          setCouponBreakdown(newBreakdown)
          setCouponMeta(meta)
          localStorage.setItem('guestCouponBreakdown', JSON.stringify(newBreakdown))
        }
        saveGuestCart(guestCart)
      }
    } catch (error) {
      throw error
    }
  }

  const removeCoupon = async (couponCode = null) => {
    try {
      if (isAuthenticated) {
        let cartData = await cartAPI.removeCoupon(couponCode)
        // Normalize
        if (!cartData.couponCodes) {
          cartData.couponCodes = cartData.couponCode ? [cartData.couponCode] : []
        }
        // Force remove the code from the array in case backend didn't clear it
        if (couponCode) {
          cartData.couponCodes = cartData.couponCodes.filter(c => c !== couponCode)
        } else {
          cartData.couponCodes = []
        }
        cartData.couponCode = null
        cartData = await refreshCouponBreakdown(cartData)
        syncCart(cartData)
        setCouponMeta(null)
      } else {
        const guestCart = { ...cart }
        guestCart.couponCodes = (guestCart.couponCodes || []).filter(c => c !== couponCode)
        const newBreakdown = couponBreakdown.filter(m => m.code !== couponCode)
        const combinedDiscount = newBreakdown.reduce(
          (sum, m) => sum + (m.freeShipping ? 0 : Number(m.discountAmount ?? 0)), 0
        )
        guestCart.discountAmount = Math.min(combinedDiscount, guestCart.subtotal)
        guestCart.total = Math.max(0, guestCart.subtotal - guestCart.discountAmount)
        setCouponBreakdown(newBreakdown)
        if (newBreakdown.length === 0) {
          delete guestCart.couponCode
          localStorage.removeItem('guestCouponBreakdown')
        } else {
          localStorage.setItem('guestCouponBreakdown', JSON.stringify(newBreakdown))
        }
        saveGuestCart(guestCart)
      }
    } catch (error) {
      throw error
    }
  }

  const cartTotals = {
    subtotal: Number(cart?.subtotal ?? 0),
    total: Number(cart?.total ?? 0),
    shippingAmount: Number(cart?.shippingAmount ?? 0),
    taxAmount: Number(cart?.taxAmount ?? 0),
    discountAmount: Number(cart?.discountAmount ?? 0),
    couponCode: cart?.couponCode ?? null,
    couponCodes: cart?.couponCodes ?? [],
  }

  const value = {
    cart,
    cartTotals,
    loading,
    itemCount,
    couponMeta,
    setCouponMeta,
    couponBreakdown,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    applyCoupon,
    removeCoupon,
    loadCart,
    refreshCart: loadCart,
  }

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  )
}